import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Backdrop,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Styles
import "../OpdStepper.css";

const inputFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px !important",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease",
    fontSize: "13.5px",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: "#1E88E5", borderWidth: "1.5px" },
    "&.Mui-disabled": {
      backgroundColor: "#F9FAFB",
      "& fieldset": { borderColor: "#E5E7EB" },
    },
  },
  "& .MuiOutlinedInput-input::placeholder": { color: "#9CA3AF", opacity: 1 },
};

export const diagnosticTestsValidationSchema = Yup.object({
  tests: Yup.array().of(
    Yup.object({
      id: Yup.number().nullable(),
      testName: Yup.string().required("Test name is required"),
      selected: Yup.boolean(),
      resultValue: Yup.string().trim().max(120, "Result cannot exceed 120 characters"),
      status: Yup.number().oneOf([1, 3, 4]),
    })
  ),
});

export default function DiagnosticTests({
  nextStep,
  prevStep,
  initialValues,
  submitLabel = "Next",
  showActions = true,
}) {
  const [loading, setLoading] = useState(true);
  const [masterTests, setMasterTests] = useState([]);
  const [savedTests, setSavedTests] = useState(null);

  // Fetch pathology tests from master API and saved step data on mount
  useEffect(() => {
    let active = true;
    const fetchTestsAndSaved = async () => {
      setLoading(true);
      try {
        const displayId = localStorage.getItem("currentOpdDisplayId") || "";
        const [masterRes, stepRes] = await Promise.all([
          eopdAPI.getPathologyTests(),
          displayId ? eopdAPI.getOpdStepData(displayId, 5).catch(() => null) : null,
        ]);

        if (!active) return;
        if (masterRes && masterRes.success && Array.isArray(masterRes.tests)) {
          setMasterTests(masterRes.tests);
        } else {
          toast.error("Failed to load pathology tests.");
        }

        if (stepRes && stepRes.success && stepRes.data) {
          const loaded = Array.isArray(stepRes.data.lab_tests)
            ? stepRes.data.lab_tests
            : Array.isArray(stepRes.data.tests)
            ? stepRes.data.tests
            : [];
          setSavedTests(loaded);
        }
      } catch (err) {
        console.error("Error loading pathology tests:", err);
        if (active) toast.error("Failed to load pathology tests.");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchTestsAndSaved();
    return () => {
      active = false;
    };
  }, []);

  // Map master tests to initial values for Formik
  const initialFormValues = useMemo(() => {
    if (initialValues?.tests && initialValues.tests.length > 0) {
      return initialValues;
    }
    const savedMap = new Map();
    if (Array.isArray(savedTests)) {
      savedTests.forEach((st) => {
        const id = st.test_id ?? st.id;
        if (id) savedMap.set(id, st);
      });
    }

    const mapped = masterTests.map((t) => {
      const existing = savedMap.get(t.id);
      return {
        id: t.id,
        testName: t.test_name,
        selected: Boolean(existing),
        resultValue: existing?.result_val ?? existing?.resultValue ?? "",
        status: existing?.status ?? 1, // Default to Pending (1)
        isCustom: false,
      };
    });
    return { tests: mapped };
  }, [masterTests, initialValues, savedTests]);

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={diagnosticTestsValidationSchema}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        try {
          const displayId = localStorage.getItem("currentOpdDisplayId") || "";
          const payload = {
            display_id: displayId,
            tests: (values.tests || [])
              .filter((t) => t.selected)
              .map((t) => ({
                test_id: t.id,
                result_val: String(t.resultValue || "").trim(),
                status: Number(t.status) || 1,
              })),
          };
          const response = await eopdAPI.saveOpdStep(5, payload);
          if (response && response.success) {
            toast.success(response.message || "Lab Tests prescribed successfully!");
            nextStep();
          } else {
            toast.error(response.message || "Failed to prescribe lab tests.");
          }
        } catch (error) {
          console.error(error);
          const errMsg = error.response?.data?.message || error.message || "Failed to prescribe lab tests.";
          toast.error(errMsg);
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, setFieldValue, handleBlur, isSubmitting }) => {
        const rows = values.tests || [];
        const selectedCount = rows.filter((r) => r.selected).length;
        const allSelected = rows.length > 0 && selectedCount === rows.length;

        const toggleAll = (checked) => {
          const mapped = rows.map((r) => ({
            ...r,
            selected: checked,
            ...(!checked ? { resultValue: "", status: 1 } : {}),
          }));
          setFieldValue("tests", mapped);
        };

        const toggleRow = (i, checked) => {
          setFieldValue(`tests[${i}].selected`, checked);
          if (!checked) {
            setFieldValue(`tests[${i}].resultValue`, "");
            setFieldValue(`tests[${i}].status`, 1);
          }
        };

        return (
          <Form noValidate>
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isSubmitting}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <Typography className="opd-step-title">
              Lab Tests
            </Typography>

            {/* Flat Card Container for Tests Table */}
            <Box className="opd-diagnosis-card">
              <Typography className="opd-diagnosis-card-title">
                Tests
              </Typography>
              {/* <Typography className="opd-diagnosis-card-subtitle">
                Defaults reflect commonly performed HWC point-of-care tests — edit, remove, or add rows to match what’s available at this facility.
              </Typography> */}

              {loading ? (
                <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
                  <CircularProgress size={40} />
                </Stack>
              ) : (
                <TableContainer sx={{ border: "1px solid #D8DFEA", borderRadius: "8px" }}>
                  <Table
                    size="small"
                    sx={{
                      /* har cell par ek jaisi grid line */
                      "& th, & td": {
                        borderBottom: "1px solid #D8DFEA",
                        borderRight: "1px solid #D8DFEA",
                      },
                      "& th:last-of-type, & td:last-of-type": { borderRight: 0 },
                      "& tbody tr:last-of-type td": { borderBottom: 0 },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F7F9FC" }}>
                        <TableCell padding="checkbox" sx={{ py: "12px", pl: "16px" }}>
                          <Checkbox
                            size="small"
                            disabled={isSubmitting || rows.length === 0}
                            checked={allSelected}
                            indeterminate={selectedCount > 0 && !allSelected}
                            onChange={(e) => toggleAll(e.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "11px", fontWeight: 700, color: "#2C3A57", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                          Test Parameter
                        </TableCell>
                        <TableCell sx={{ fontSize: "11px", fontWeight: 700, color: "#2C3A57", letterSpacing: "0.5px", textTransform: "uppercase", width: "40%" }}>
                          Result Value
                        </TableCell>
                        <TableCell sx={{ fontSize: "11px", fontWeight: 700, color: "#2C3A57", letterSpacing: "0.5px", textTransform: "uppercase", width: "30%" }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ width: 48 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => {
                        const resErr = getIn(errors, `tests[${i}].resultValue`);
                        const resTouched = getIn(touched, `tests[${i}].resultValue`);
                        const nameErr = getIn(errors, `tests[${i}].testName`);
                        const nameTouched = getIn(touched, `tests[${i}].testName`);
                        const hasResErr = Boolean(resTouched && resErr);
                        const hasNameErr = Boolean(nameTouched && nameErr);

                        return (
                          <TableRow key={row.id ?? `custom-${i}`} sx={{ "& td": { py: "10px", verticalAlign: "middle" }, "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" } }}>
                            <TableCell padding="checkbox" sx={{ pl: "16px" }}>
                              <Checkbox
                                size="small"
                                disabled={isSubmitting}
                                checked={Boolean(row.selected)}
                                onChange={(e) => toggleRow(i, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              {row.isCustom ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={!row.selected || isSubmitting}
                                  name={`tests[${i}].testName`}
                                  value={row.testName}
                                  onChange={(e) => setFieldValue(`tests[${i}].testName`, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="Enter test name"
                                  error={hasNameErr}
                                  helperText={hasNameErr ? nameErr : ""}
                                  sx={inputFieldStyle}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: row.selected ? "#1F2937" : "#9CA3AF",
                                  }}
                                >
                                  {row.testName}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                disabled={!row.selected || isSubmitting}
                                name={`tests[${i}].resultValue`}
                                value={row.resultValue || ""}
                                onChange={(e) => setFieldValue(`tests[${i}].resultValue`, e.target.value)}
                                onBlur={handleBlur}
                                placeholder={row.selected ? "Enter result value..." : "Enter result value..."}
                                error={hasResErr}
                                helperText={hasResErr ? resErr : ""}
                                sx={inputFieldStyle}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                fullWidth
                                size="small"
                                disabled={!row.selected || isSubmitting}
                                value={row.status || 1}
                                onChange={(e) => setFieldValue(`tests[${i}].status`, Number(e.target.value))}
                                sx={{
                                  borderRadius: "8px !important",
                                  backgroundColor: row.selected ? "#ffffff" : "#F9FAFB",
                                  fontSize: "13.5px",
                                  "& fieldset": { borderColor: "#E5E7EB" },
                                  "&:hover fieldset": { borderColor: "#CBD5E1" },
                                  "&.Mui-focused fieldset": { borderColor: "#1E88E5", borderWidth: "1.5px" },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#F9FAFB",
                                    "& fieldset": { borderColor: "#E5E7EB" },
                                  },
                                }}
                              >
                                <MenuItem value={1}>Pending</MenuItem>
                                <MenuItem value={3}>Normal</MenuItem>
                                <MenuItem value={4}>Abnormal</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell align="center">
                              {row.isCustom && (
                                <IconButton
                                  size="small"
                                  disabled={isSubmitting}
                                  onClick={() => {
                                    const nextRows = [...rows];
                                    nextRows.splice(i, 1);
                                    setFieldValue("tests", nextRows);
                                  }}
                                  sx={{ color: "#EF4444" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Action Buttons Footer */}
            {showActions && (
              <Stack
                direction="row"
                justifyContent={prevStep ? "space-between" : "flex-end"}
                alignItems="center"
              >
                {prevStep && (
                  <Button
                    type="button"
                    variant="outlined"
                    className="opd-back-btn"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting || loading}
                  className="opd-next-btn"
                >
                  {submitLabel}
                </Button>
              </Stack>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
